import React from 'react';
import { MessageSquareIcon, TimerIcon, ShieldAlertIcon } from 'lucide-react';
const blocks = [{
  phase: 'Warm-up',
  color: 'bg-indigo',
  text: 'text-white',
  what: 'Pepper + shuffle to passing zones',
  cue: '“Platform out, move your feet”',
  min: 10
}, {
  phase: 'Serve read',
  color: 'bg-coral',
  text: 'text-white',
  what: 'Read the server, own your seam — 3-passer cup',
  cue: '“First step on the server’s contact”',
  min: 20
}, {
  phase: 'Pass to target',
  color: 'bg-teal',
  text: 'text-white',
  what: 'Pass-to-target ladder, by rotation',
  cue: '“Platform to the setter, call the seam”',
  min: 25
}, {
  phase: '6v6 game',
  color: 'bg-amber',
  text: 'text-ink',
  what: 'Serve &amp; pass for points, rotate on sideout',
  cue: '“Win the first contact, win the rally”',
  min: 25
}];
const ladder = [{
  dots: 1,
  level: 'New',
  body: 'Catch-and-call: catch the serve in your seam and call it out loud before you pass'
}, {
  dots: 2,
  level: 'Developing',
  body: 'Own your seam: pass tossed and easy serves to the right-front target'
}, {
  dots: 3,
  level: 'Advanced',
  body: 'Live float serves from across the net, passed to target under tempo'
}];
function Dots({
  n
}: {
  n: number;
}) {
  return <span className="flex gap-0.5" aria-label={`${n} of 3`} data-id="element-317" data-mp="3l54p9hyv01v4">
      {[1, 2, 3].map(i => <span key={i} className={`h-2 w-2 rounded-full ${i <= n ? 'bg-coral' : 'bg-cloud-dark'}`} data-id="element-318" data-mp="3h42pn0115nsm" />)}
    </span>;
}
function PageFrame({
  tag,
  title,
  children
}: {
  tag: string;
  title: string;
  children: React.ReactNode;
}) {
  return <article className="overflow-hidden rounded-2xl bg-white text-ink shadow-book" data-id="element-319" data-mp="cqw42uyo2dcz">
      <div className="flex items-center justify-between bg-coral px-5 py-2.5 text-white" data-id="element-320" data-mp="7xt9cji5kxv7">
        <span className="text-[10px] font-bold uppercase tracking-[0.18em]" data-id="element-321" data-mp="3y2oewfb5byl">{tag}</span>
        <span className="text-[10px] font-semibold uppercase tracking-widest text-white/80" data-id="element-322" data-mp="10fp535u5bbeg">Vol. 4 · Team Systems</span>
      </div>
      <div className="p-5 sm:p-6" data-id="element-323" data-mp="vnnleyuneiee">
        <h3 className="font-display text-xl font-extrabold leading-tight tracking-tight sm:text-2xl" data-id="element-324" data-mp="vmm96r1wbpv3">{title}</h3>
        {children}
      </div>
    </article>;
}
export function SamplePlan() {
  return <section aria-labelledby="sample-heading" className="w-full bg-cloud" data-id="element-325" data-mp="2ux8djdzk9rw6">
      <div className="mx-auto max-w-6xl px-5 py-16 sm:py-20" data-id="element-326" data-mp="2g67vch90ijmu">
        <div className="text-center" data-id="element-327" data-mp="2l4svyhs6f5bt">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-coral-deep" data-id="element-328" data-mp="2l3rn8jymuux9">Look inside</p>
          <h2 id="sample-heading" className="mt-3 font-display text-3xl font-extrabold text-ink sm:text-5xl" data-id="element-329" data-mp="277w8mqb9o5zo">
            One real session, both pages
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-slate" data-id="element-330" data-mp="1x8x7rtwqten8">
            Volume 4, Session 28: Serve-Receive Base. Page A is the practice you
            run. Page B is the drill, at three levels, with the cue, the #1 error
            and the fix. Every one of the 52 sessions looks like this.
          </p>
        </div>

        <div className="mt-10 grid gap-5 lg:grid-cols-2" data-id="element-331" data-mp="d96bj9x17sr">
          <PageFrame tag="Page A · Run Sheet" title="Session 28: Serve-Receive Base" data-id="element-332" data-mp="1kyfol3izvskh">
            <dl className="mt-3 grid gap-1.5 text-[13px] text-slate" data-id="element-333" data-mp="1fz2srta5bb5r">
              <div data-id="element-334" data-mp="1un7d11oujuqn"><dt className="inline font-semibold text-ink" data-id="element-335" data-mp="1umxl38k8rdib">Objective: </dt><dd className="inline" data-id="element-336" data-mp="1uoif8x2n8ufn">a 3-passer receive that passes to target from all six rotations.</dd></div>
              <div data-id="element-337" data-mp="1pue8adtdomek"><dt className="inline font-semibold text-ink" data-id="element-338" data-mp="1punyo2ic4vks">Group split: </dt><dd className="inline" data-id="element-339" data-mp="23mkmhucz0jbd">three primary passers in a cup, setter at the right-front target.</dd></div>
              <div data-id="element-340" data-mp="1zlr39eah3sot"><dt className="inline font-semibold text-ink" data-id="element-341" data-mp="1zm0sv0rlvxtz">Clock: </dt><dd className="inline" data-id="element-342" data-mp="2dmbik0h6j6dk">Full 90 / Med 60 / Short 45 — minutes already worked out.</dd></div>
            </dl>
            <ul className="mt-4 grid gap-2" data-id="element-343" data-mp="28o9zhfjr7723">
              {blocks.map(s => <li key={s.phase} className="grid grid-cols-[84px_1fr_auto] items-start gap-3 rounded-xl border border-cloud-dark p-2.5" data-id="element-344" data-mp="j790gnugmqko">
                  <span className={`rounded-lg px-2 py-1 text-center text-[10px] font-bold uppercase tracking-wider ${s.color} ${s.text}`} data-id="element-345" data-mp="easd3vv34g8n">{s.phase}</span>
                  <span className="text-[12.5px] leading-snug text-ink" data-id="element-346" data-mp="koelqkkt5jbs">
                    <span dangerouslySetInnerHTML={{
                  __html: s.what
                }} data-id="element-347" data-mp="poapciplf8qt" />
                    <span className="mt-0.5 block text-[12px] italic text-slate" data-id="element-348" data-mp="pm7rjci78bbj">{s.cue}</span>
                  </span>
                  <span className="flex items-center gap-1 text-[11px] font-bold text-slate" data-id="element-349" data-mp="3t513jkvbws3l">
                    <TimerIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-350" data-mp="3t36im6k24zzi" />
                    {s.min}′
                  </span>
                </li>)}
            </ul>
            <p className="mt-3 text-[13px] text-slate" data-id="element-351" data-mp="6vq2bhwkrxux">
              <span className="font-semibold text-ink" data-id="element-352" data-mp="bornu8raevnh">Wrap (last 10 min):</span> fill the receive worksheet, name the one rotation that scored mostly shanks, pick the single fix for next week.
            </p>
          </PageFrame>

          <PageFrame tag="Page B · Drill Card" title="Read the Server — Own Your Seam" data-id="element-353" data-mp="3fbrwhquusio8">
            <p className="mt-2 text-[13px] text-slate" data-id="element-354" data-mp="1whyhdthsxmlv">
              The serve is won before it arrives: scan the server, take your first step on contact, and call the seam that&apos;s yours.
            </p>
            <p className="mt-4 text-[11px] font-bold uppercase tracking-widest text-coral-deep" data-id="element-355" data-mp="1nfyz6xv1wchc">By level</p>
            <ol className="mt-2 grid gap-2" data-id="element-356" data-mp="1iigwhp7mmths">
              {ladder.map(l => <li key={l.level} className="rounded-xl bg-cloud p-3" data-id="element-357" data-mp="1dl72dw9altnm">
                  <div className="flex items-center gap-2" data-id="element-358" data-mp="18snmgsn4umfn">
                    <Dots n={l.dots} data-id="element-359" data-mp="18r2wzhfodton" />
                    <span className="text-[12px] font-bold uppercase tracking-wider text-ink" data-id="element-360" data-mp="14qiudycc1xv4">{l.level}</span>
                  </div>
                  <p className="mt-1 text-[12.5px] leading-snug text-slate" data-id="element-361" data-mp="zu2axhg2w7od">{l.body}</p>
                </li>)}
            </ol>
            <div className="mt-4 grid gap-2 text-[12.5px]" data-id="element-362" data-mp="pubevbbvdkm6">
              <p className="flex gap-2 text-ink" data-id="element-363" data-mp="puthqqt1ex32">
                <MessageSquareIcon className="mt-0.5 h-4 w-4 flex-none text-amber-deep" aria-hidden="true" data-id="element-364" data-mp="g5wchpmwki41" />
                <span data-id="element-365" data-mp="l5sfpmns05ij"><span className="font-semibold" data-id="element-366" data-mp="l6k0s3jqykuk">Top cue:</span> “Scan the server — first step on contact — call your seam.”</span>
              </p>
              <p className="flex gap-2 text-ink" data-id="element-367" data-mp="783w0i5g34f2">
                <span className="mt-0.5 flex h-4 w-4 flex-none items-center justify-center rounded-full bg-coral text-[9px] font-bold text-white" data-id="element-368" data-mp="b8e1ltpbrste">!</span>
                <span data-id="element-369" data-mp="yz9rume4ufyj"><span className="font-semibold" data-id="element-370" data-mp="13w0ejskg09sx">#1 error → fix:</span> late first step. Move on the server&apos;s contact, not when the ball crosses the net.</span>
              </p>
              <p className="flex gap-2 text-ink" data-id="element-371" data-mp="q2ivs9zkz13y">
                <ShieldAlertIcon className="mt-0.5 h-4 w-4 flex-none text-teal-deep" aria-hidden="true" data-id="element-372" data-mp="u5fb9rttqh3o" />
                <span data-id="element-373" data-mp="1huqc19b6cbht"><span className="font-semibold" data-id="element-374" data-mp="1huzrw42i929s">Safety:</span> pain, head knock or a bad landing → stop and send the player to the athletic trainer.</span>
              </p>
            </div>
          </PageFrame>
        </div>

        <p className="mt-6 text-center text-[13px] text-slate" data-id="element-375" data-mp="37b3u3qaejgt6">
          Volume 4 of 6 · 9 weekly sessions + a worksheet, Coach&apos;s Notes and Quick Wins in every volume
        </p>
      </div>
    </section>;
}